/**
 * Deterministic RNG v1.0
 *
 * Provides a seeded pseudo-random number generator for deterministic
 * adaptive engine behavior. Two identical states + seeds will always
 * produce identical question selections.
 *
 * Uses mulberry32 algorithm — fast, well-distributed, 32-bit state.
 *
 * Usage:
 *   const rng = createSessionRng('Metriek', 7, Date.now());
 *   rng.random();        // 0..1 (like Math.random)
 *   rng.shuffle(array);  // Fisher-Yates with seeded RNG
 *   rng.pick(array);     // Random element from array
 *   rng.chance(0.7);     // true with 70% probability
 */

export interface SeededRng {
  /** Returns a float in [0, 1) — drop-in replacement for Math.random() */
  random(): number;
  /** Fisher-Yates shuffle using seeded RNG. Returns new array. */
  shuffle<T>(array: T[]): T[];
  /** Pick a random element from array. Returns undefined if empty. */
  pick<T>(array: T[]): T | undefined;
  /** Returns true with given probability (0..1) */
  chance(probability: number): boolean;
  /** Pick a random integer in [min, max] (inclusive) */
  int(min: number, max: number): number;
  /** Get the current seed (for debugging/logging) */
  readonly seed: number;
}

/**
 * Mulberry32 — fast 32-bit seeded PRNG.
 * Period: 2^32. Passes BigCrush statistical tests.
 */
function mulberry32(seed: number): () => number {
  let state = seed | 0;
  return function (): number {
    state = state + 0x6d2b79f5 | 0;
    let t = Math.imul(state ^ state >>> 15, 1 | state);
    t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
}

/**
 * Create a numeric seed from a string using djb2 hash.
 */
function hashString(str: string): number {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) + hash + str.charCodeAt(i) | 0;
  }
  return hash >>> 0; // Ensure unsigned 32-bit
}

/**
 * Combine multiple seed components into a single numeric seed.
 * Order matters — different orderings produce different seeds.
 */
function combineSeed(...parts: (string | number)[]): number {
  const combined = parts.map(String).join('|');
  return hashString(combined);
}

/**
 * Create a seeded RNG for a practice session.
 *
 * @param moduleId - The module being practiced
 * @param groupId - The school group (6, 7, 8)
 * @param sessionSeed - Session-specific seed (e.g., timestamp, session counter)
 * @param userId - Optional user identifier for per-user determinism
 */
export function createSessionRng(moduleId: string, groupId: number, sessionSeed: number | string, userId?: string): SeededRng {
  const seed = combineSeed(moduleId, groupId, sessionSeed, userId ?? 'default');
  return createRng(seed);
}

/**
 * Create a seeded RNG from a raw numeric seed.
 */
export function createRng(seed: number): SeededRng {
  const next = mulberry32(seed);
  const rng: SeededRng = {
    get seed() {
      return seed;
    },
    random(): number {
      return next();
    },
    shuffle<T>(array: T[]): T[] {
      const shuffled = [...array];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(next() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      return shuffled;
    },
    pick<T>(array: T[]): T | undefined {
      if (array.length === 0) return undefined;
      return array[Math.floor(next() * array.length)];
    },
    chance(probability: number): boolean {
      return next() < probability;
    },
    int(min: number, max: number): number {
      return min + Math.floor(next() * (max - min + 1));
    }
  };
  return rng;
}